import React, { useEffect, useMemo, useState } from "react";
import { useForm, useFieldArray, useWatch } from "react-hook-form";
import Modal from "@/components/Modal/Modal";
import formRenderer from "@/lib/formRenderer";
import {
  getInitialValues,
  requiredMessage,
  onlyNumbersInput,
  pick,
  amountValidation,
  today,
  onlyNumbersInputWithDecimal,
  toUTCISOStringWithCurrentTime,
} from "@/lib/utils";
import AuthService from "@/services/api/auth/auth";
import toast from "@/lib/toast";
import styles from "./DeliverOrderModal.module.scss";
import MapView from "@/components/Map/MapView";
import { Divider } from "@mui/material";
import { dateFormatter } from "@/components/DateFormat/DateFormat";

interface ApprovedSalesDeliveryModalProps {
  isOpen: boolean;
  onClose: () => void;
  orderData: any;
  onSuccess?: () => void;
  retailerName?: any;
}

interface SalesFormValues {
  orderId: number,
  purchasedAmount: number | string;
  shippingCharges: number | string;
  taxPercent: number | string;
  taxAmount: number | string;
  totalAmount: number | string;
  amountPaid: number | string;
  saleDate: string;
  retailerName: string;
  Address: string;
  latitude: number;
  longitude: number;
  quantity: number | string;
  orderDate: string;

  products: {
    warehouseId: number | string;
    productId: number | string;
    quantity: number | string;
    deliverQty: number | string;
    unitPrice: number | string;
    skuCode?: string;
  }[];
}

const ApprovedSalesDeliveryModal: React.FC<ApprovedSalesDeliveryModalProps> = ({
  isOpen,
  onClose,
  orderData,
  onSuccess,
  retailerName
}) => {
  const [mapLocation, setMapLocation] = useState({ lat: 0, lng: 0 });
  const [mapModalOpen, setMapModalOpen] = useState(false);
  const [warehouseOptions, setWarehouseOptions] = useState<any[]>([]);
  const [warehouseProductMap, setWarehouseProductMap] = useState<Record<any, any[]>>({});
  const [selectedAddress, setSelectedAddress] = useState("");

  const currency = localStorage.getItem("currency");

  const formConfig = useMemo(
    () => ({
      purchasedAmount: {
        defaultValue: 0,
        controlType: "input",
        label: `Total Sales (${currency})`,
        isDisabled: true,
        isRequired: true,
      },
      shippingCharges: {
        defaultValue: 0,
        controlType: "input",
        label: "Shipping and Handling Charges",
        validations: {
          required: requiredMessage("Shipping Charges"),
          // pattern: {
          //   value: /^\d{1,15}(\.\d{1,2})?$/,
          //   message: "Max 15 digits allowed",
          // },
        },
        props: {
          type: "number",
          onKeyDown: onlyNumbersInputWithDecimal,
        },
        isRequired: true,
      },
      taxPercent: {
        defaultValue: 0,
        controlType: "input",
        label: "Tax (%)",
        validations: {
          required: requiredMessage("Tax"),
        },
        props: {
          type: "number",
          onKeyDown: onlyNumbersInputWithDecimal,
        },
        isRequired: true,
      },
      taxAmount: {
        defaultValue: 0,
        controlType: "input",
        label: "Tax Amount",
        isDisabled: true,
      },
      totalAmount: {
        defaultValue: 0,
        controlType: "input",
        label: `Invoice Amount (${currency})`,
        isDisabled: true,
      },
      amountPaid: {
        defaultValue: 0,
        controlType: "input",
        label: `Amount Received (${currency})`,
        validations: {
          required: requiredMessage("Amount Paid"),
          pattern: {
            value: /^\d{1,15}(\.\d{1,2})?$/,
            message: "Max 15 digits allowed",
          },
          validate: (value: any) => {
            const invoiceAmount = Number(getValues("totalAmount")) || 0;

            if (Number(value) > invoiceAmount) {
              return "Amount received cannot be greater than invoice amount";
            }
            return true;
          },
        },
        props: {
          type: "number",
          onKeyDown: onlyNumbersInputWithDecimal
        },
        isRequired: true,
      },
      saleDate: {
        controlType: "date",
        label: "Sales Date",
        placeholder: "Enter Date",
        isRequired: true,
        props: {
          min: today,
        },
        validations: { required: requiredMessage("Sales Date") }
      },
      retailerName: {
        defaultValue: "",
        controlType: "input",
        label: "Retailer Name",
        isRequired: true,
        isDisabled: true,
      },
      Address: {
        defaultValue: "",
        validations: { required: requiredMessage("Address") },
        controlType: "input",
        label: "Address",
        isRequired: true,
        onFieldClick: () => setMapModalOpen(true),
        className: styles.fullRow,
      },
      Latitude: {
        defaultValue: "",
        controlType: "hidden",
        label: "Latitude",
      },
      Longitude: {
        defaultValue: "",
        controlType: "hidden",
        label: "Longitude",
      },
    }),
    []
  );

  const getFilteredProducts = (warehouseId: any, rowIndex: number) => {
    const allProducts = warehouseProductMap[warehouseId] || [];
    const selectedProductIds = watchedProducts
      .filter((_: any, i: number) => i !== rowIndex)
      .filter((p: any) => Number(p.warehouseId) === Number(warehouseId))
      .map((p: any) => Number(p.productId));

    return allProducts.filter(
      (p: any) => !selectedProductIds.includes(Number(p.value))
    );
  };

  const { register, handleSubmit, control, watch, setValue, getValues, formState, reset, trigger } =
    useForm<SalesFormValues>({
      defaultValues: {
        ...getInitialValues(formConfig),
        products: [
          {
            warehouseId: "",
            productId: "",
            quantity: "",
            deliverQty: "",
            unitPrice: "",
          },
        ],
      },
      mode: "onChange",
    });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "products",
  });

  const totalAmount = watch("totalAmount");
  const amountPaid = watch("amountPaid");
  const watchedProducts = useWatch({ control, name: "products" });
  const watchedShippingCharges = useWatch({ control, name: "shippingCharges" });
  const watchedTaxPercent = useWatch({ control, name: "taxPercent" });

  const fetchWarehouseByOrder = async (orderId: number) => {
    try {
      const res = await AuthService.getspecificorderDetails(orderId)
      if (res?.status === 404) {
      toast.error("Product is not available in any warehouse");
      onClose(); 
      return;
    }
      const warehouses = res?.data || [];

      const warehouseOpts = warehouses.map((w: any) => ({
        label: w.warehouseCode,
        value: w.warehouseId,
      }));

      const map: Record<number, any[]> = {};
      warehouses.forEach((w: any) => {
        map[w.warehouseId] = w.products.map((p: any) => ({
          label: p.productName,
          value: p.productId,
          skuCode: p.skuCode,
          quantity: p.quantity,
          sellingPrice: p.sellingPrice,
          availableQuantity: p.availableQuantity
        }));
      });

      setWarehouseOptions(warehouseOpts);
      setWarehouseProductMap(map);
    } catch (err : any) {
      if (err?.res?.status === 404) {
      toast.error("Product is not available in any warehouse");
    } else {
      toast.error("Failed to load warehouse details");
    }
    onClose();
    }
  };

  useEffect(() => {
    if (isOpen && orderData?.id) {
      fetchWarehouseByOrder(orderData.id);
    }
  }, [isOpen, orderData]);


  useEffect(() => {

    watchedProducts?.forEach((row, index) => {

      const warehouseId = row?.warehouseId;
      const productId = row?.productId;

      if (!warehouseId || !productId) return;

      const selectedProduct =
        warehouseProductMap?.[warehouseId]?.find(
          (p: any) =>
            Number(p.value) === Number(productId)
        );

      if (!selectedProduct) return;


      if (Number(productId) === Number(orderData.productId)) {

        setValue(
          `products.${index}.quantity`,
          orderData.quantity
        );

        setValue(
          `products.${index}.unitPrice`,
          orderData.price
        );

        setValue(
          `products.${index}.skuCode`,
          orderData.skuCode
        );

      }

      const currentDeliverQty =
        getValues(`products.${index}.deliverQty`);

      if (!currentDeliverQty) {

        setValue(
          `products.${index}.deliverQty`,
          selectedProduct.availableQuantity
        );
      }
    });

  }, [
    watchedProducts?.map(p => p.productId).join(),
    watchedProducts?.map(p => p.warehouseId).join(),
    warehouseProductMap,
    orderData
  ]);


  useEffect(() => {
    if (isOpen && orderData) {
      const initialProducts =
        orderData && Array.isArray(orderData)
          ? orderData.map((detail: any) => ({
            productId: detail.productId || detail.distributorProductId,
            quantity: detail.quantity || 0,
            unitPrice: detail.price || 0,
            skuCode: detail.skuCode || "",
          }))
          : [{ warehouseId: "", productId: "", quantity: "", unitPrice: "", deliverQty: "" }];
      reset({
        ...getInitialValues(formConfig),
        orderId: orderData.id,
        retailerName: retailerName || "",
        quantity: orderData.quantity || "",
        orderDate : orderData.orderDate || "",
        products: initialProducts,
        amountPaid: 0,
      });
      console.log("MODAL orderData", orderData);
      // setValue("retailerName", orderData.retailerName || "");
    }
  }, [isOpen, orderData, reset, formConfig]);

  useEffect(() => {
    const purchasedAmount = watchedProducts.reduce((sum, row) => {
      const deliveredQty = Number(row?.deliverQty) || 0;
      const unitPrice = Number(row?.unitPrice) || 0;
      return sum + deliveredQty * unitPrice;
    }, 0);

    setValue("purchasedAmount", purchasedAmount, { shouldValidate: false });

    const shippingCharges = Number(watchedShippingCharges) || 0;
    const taxPercent = Number(watchedTaxPercent) || 0;
    const taxAmount = purchasedAmount * taxPercent / 100;

    setValue("taxAmount", taxAmount, { shouldValidate: false });
    const totalAmount = purchasedAmount + shippingCharges + taxAmount;
    setValue("totalAmount", totalAmount, { shouldValidate: false });
    setValue("amountPaid", totalAmount, { shouldValidate: false });
  }, [JSON.stringify(watchedProducts), watchedShippingCharges, watchedTaxPercent, setValue]);

  const getProductRowConfig = (index: number) => ({
    [`products.${index}.warehouseId`]: {
      controlType: "select",
      label: "Warehouse Code",
      placeholder: "Select Warehouse",
      options: warehouseOptions,
      isControlled: true,
      isRequired: true,
      validations: { required: requiredMessage("Warehouse Code") },
      onChange: () => {
        setValue(`products.${index}.productId`, "");
        setValue(`products.${index}.quantity`, "");
        setValue(`products.${index}.deliverQty`, "");
      },
    },
    [`products.${index}.productId`]: {
      controlType: "select",
      label: "Product Code",
      placeholder: "Select Product",
      isControlled: true,
      isRequired: true,
      options: getFilteredProducts(
        watch(`products.${index}.warehouseId`),
        index
      ),
      validations: {
        required: requiredMessage("Product"),
      },
    },

    [`products.${index}.quantity`]: {
      defaultValue: "0",
      controlType: "input",
      label: "Ordered Quantity",
      isControlled: true,
      placeholder: "",
      isRequired: true,
      isDisabled: true,
      props: {
        type: "number",
        onKeyDown: onlyNumbersInput,
      },
      validations: {
        required: requiredMessage("Quantity"),
        validate: (value: any) => {
          if (value <= 0)
            return "Quantity must be greater than 0";

          if (value.toString().length > 10)
            return "Quantity cannot exceed 10 digits";

          return true;
        },
      },
    },
    [`products.${index}.deliverQty`]: {
      controlType: "input",
      label: "Delivered Quantity",
      isRequired: true,
      isControlled: true,
      props: {
        type: "number",
        onKeyDown: onlyNumbersInput,
      },
      validations: {
        required: requiredMessage("Deliver Quantity"),
        validate: (value: any) => {

          const productId = watch(`products.${index}.productId`);
          if (!productId) return true;

          const deliverQty = Number(value);
          if (deliverQty <= 0)
            return "Quantity must be greater than 0";

          const warehouseId = watch(`products.${index}.warehouseId`);

          const selectedProduct =
            warehouseProductMap?.[warehouseId]?.find(
              (p: any) => Number(p.value) === Number(productId)
            );

          const availableQty =
            Number(selectedProduct?.availableQuantity) || 0;

          if (deliverQty > availableQty)
            return "Exceeds warehouse stock";

          const rows = watch("products");

          const totalDeliverQty = rows.reduce(
            (sum, row) => sum + Number(row.deliverQty || 0),
            0
          );

          const orderedQty = Number(orderData.quantity) || 0;

          if (totalDeliverQty > orderedQty)
            return "Delivered quantity exceeds order quantity";

          const remainingQty = orderedQty - totalDeliverQty;

          const lastSelectedIndex = rows
            .map((row, i) => row.productId ? i : -1)
            .filter(i => i !== -1)
            .pop();

          if (totalDeliverQty < orderedQty && index === lastSelectedIndex)
            return `Select other warehouse to fulfill remaining quantity of ${remainingQty}`;

          return true;
        }
      }
    },
    [`products.${index}.unitPrice`]: {
      defaultValue: "",
      controlType: "input",
      label: `Selling Unit Price (${currency})`,
      placeholder: "Enter Unit Price",
      isRequired: true,
      isControlled: true,
      props: {
        type: "number",
        onKeyDown: onlyNumbersInputWithDecimal,
      },
      validations: { required: requiredMessage("selling price") }
    },

  });

  const onSubmit = async (values: any) => {
    try {
      const invoicePayload = {
        invoiceNumber: "",
        orderId: Number(orderData.id),
        salesDate: toUTCISOStringWithCurrentTime(values.saleDate),
        shippingAddress: values.Address,
        shippingHandlingFees: Number(values.shippingCharges || 0),
        Latitude: values.latitude,
        Longitude: values.longitude,
        taxPercentage: Number(values.taxPercent || 0),
        taxAmount: Number(values.taxAmount || 0),
        totalSales: Number(values.purchasedAmount || 0),
        invoiceAmount: Number(values.totalAmount || 0),
        amountReceived: Number(values.amountPaid || 0),
        retailerName: orderData.retailerName,
        retailerEmailId: values.retailerEmailId,
        salesItems: values.products.map((product: any) => {
          const warehouseProducts = warehouseProductMap[product.warehouseId] || [];
          const selectedProduct = warehouseProducts.find((p: any) => Number(p.value) === Number(product.productId));

          return {
            warehouseId: Number(product.warehouseId || 0),
            productId: Number(product.productId),
            productName: selectedProduct?.label || "",
            skuCode: selectedProduct?.skuCode || "",
            deliveredQty: Number(product.deliverQty),
            sellingPrice: Number(product.unitPrice),
          };
        }),
      };

      const response = await AuthService.AddDeliveredOrder(invoicePayload);
      if (response?.status === 200) {
        toast.success("Order delivered successfully!");
        handleClose();
        onSuccess?.();
      } else if (response.status === 417) {
        toast.error(response?.error || "Failed to deliver order");
      }
    } catch (error: any) {
      toast.error(
        error?.response?.data?.error ||
        error?.message ||
        "Failed to deliver order"
      );
    }
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  useEffect(() => {

    watchedProducts.forEach((row, index) => {
      if (row?.productId) {
        trigger(
          `products.${index}.deliverQty`
        );
      }
    });

  }, [JSON.stringify(watchedProducts)]);
  const handleSelectLocation = (lat: number, lng: number, addr: string) => {
    setMapLocation({ lat, lng });
    setSelectedAddress(addr);

    setValue("Address", addr, {
      shouldValidate: true,
      shouldDirty: true
    });

    setValue("latitude", lat);
    setValue("longitude", lng);
    setMapModalOpen(false);
  };

  const handleMapModalClose = () => {
    setValue("Address", "");
    setValue("latitude", 0);
    setValue("longitude", 0);
    setMapModalOpen(false);
  }

  return (
    <>
    {isOpen && warehouseOptions.length > 0 && (
    <Modal
      modalOpen={isOpen}
      closeModal={handleClose}
      heading="Add Sales"
      width="1200px"
      saveButtonText="Add"
      onSave={handleSubmit(onSubmit)}
      isSubmitting={formState.isSubmitting}
    >
      <form onSubmit={handleSubmit(onSubmit)} className={styles.formContainer}>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {formRenderer(
            pick(formConfig, [
              "saleDate",
              "retailerName",
              "Address",
            ]),
            formState,
            register,
            control
          )}
        </div>
        <Divider sx={{ marginTop: 2, marginBottom: 2 }}></Divider>
        {/* Products */}
        <h4>Add Products</h4>
        {fields.map((field, index) => {
          const row = watchedProducts[index];
          const isRowFilled = row?.productId && row?.quantity;
          const isLastRow = index === fields.length - 1;
          const canDelete = fields.length > 1;
          const showAddButton = isLastRow && isRowFilled;

          return (
            <div key={field.id}>
              <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
                {formRenderer(
                  getProductRowConfig(index),
                  formState,
                  register,
                  control
                )}
              </div>

              <div className={styles.rowActions}>
                {showAddButton && (
                  <span
                    className={styles.addMore}
                    onClick={() =>
                      append({
                        warehouseId: "",
                        productId: "",
                        quantity: "",
                        unitPrice: "",
                        deliverQty: "",
                      })
                    }
                  >
                    + Add more
                  </span>
                )}

                {canDelete && (
                  <span
                    className={styles.deleteRow}
                    onClick={() => remove(index)}
                  >
                    Delete
                  </span>
                )}
              </div>
            </div>
          );
        })}


        {/* Summary */}
        {/* <h4>Order Details</h4> */}
        <Divider sx={{ marginTop: 2, marginBottom: 2 }}></Divider>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          {formRenderer(
            pick(formConfig, [
              "purchasedAmount",
              "shippingCharges",
              "taxPercent",
              "taxAmount",
              "totalAmount",
              "amountPaid",
            ]),
            formState,
            register,
            control
          )}
        </div>
      </form>
      <Modal
        modalOpen={mapModalOpen}
        closeModal={handleMapModalClose}
        heading="Select Address on Map"
        width="800px"
        onSave={() => setMapModalOpen(false)}
      >
        <MapView
          defaultLat={mapLocation.lat}
          defaultLng={mapLocation.lng}
          onSelectLocation={handleSelectLocation}
          showSearchBar={true}
        />
      </Modal>
    </Modal>
    )}
    </>
  );
};

export default ApprovedSalesDeliveryModal;
